import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { Bell, Warning, UserPlus, CreditCard, Image, VideoCamera, ArrowsClockwise, CheckCircle } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const EVENT_ICONS = {
  new_user: { icon: UserPlus, color: '#22d3ee' },
  subscription_upgrade: { icon: CreditCard, color: '#c8f060' },
  video_failed: { icon: VideoCamera, color: '#ef4444' },
  character_failed: { icon: Image, color: '#f59e0b' },
  content_failed: { icon: Warning, color: '#ef4444' },
  error: { icon: Warning, color: '#ef4444' },
};

export default function AdminNotificationsPage() {
  const [events, setEvents] = useState([]);
  const [unread, setUnread] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('bf_access_token') || localStorage.getItem('token');
    if (!token) return;
    try {
      const [evRes, stRes] = await Promise.all([
        axios.get(`${API}/api/admin/notifications?limit=100`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/admin/dashboard-stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setEvents(evRes.data.events || []);
      setUnread(evRes.data.unread_count || 0);
      setStats(stRes.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const markRead = async () => {
    const token = localStorage.getItem('bf_access_token') || localStorage.getItem('token');
    await axios.post(`${API}/api/admin/notifications/mark-read`, {}, { headers: { Authorization: `Bearer ${token}` } });
    setUnread(0);
    setEvents(prev => prev.map(e => ({ ...e, read: true })));
  };

  const C = {
    bg: '#080808', card: '#0c0c0c', text: '#f5f0e8',
    dim: 'rgba(245,240,232,0.3)', border: 'rgba(255,255,255,0.06)',
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto py-8 px-4 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bell size={20} weight="fill" style={{ color: '#a78bfa' }} />
            <h1 className="text-xl font-bold" style={{ color: C.text }}>Admin Dashboard</h1>
            {unread > 0 && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>
                {unread} new
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {unread > 0 && (
              <button onClick={markRead} data-testid="mark-read-btn"
                className="px-3 py-1.5 rounded-lg text-[11px] font-medium flex items-center gap-1.5"
                style={{ background: 'rgba(200,240,96,0.06)', border: `1px solid rgba(200,240,96,0.15)`, color: '#c8f060' }}>
                <CheckCircle size={12} /> Mark all read
              </button>
            )}
            <button onClick={fetchData} className="p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}` }}>
              <ArrowsClockwise size={14} style={{ color: C.dim }} />
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Total Users', value: stats.total_users, color: '#22d3ee' },
              { label: 'Flyers Created', value: stats.total_content?.flyers || 0, color: '#c8f060' },
              { label: 'Characters', value: stats.total_content?.characters || 0, color: '#a78bfa' },
              { label: 'Videos', value: stats.total_content?.videos || 0, color: '#f59e0b' },
            ].map(s => (
              <div key={s.label} className="p-4 rounded-xl" style={{ background: C.card, border: `1px solid ${C.border}` }}>
                <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: C.dim }}>{s.label}</p>
                <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Failure Stats */}
        {stats?.failures && (stats.failures.videos > 0 || stats.failures.characters > 0) && (
          <div className="p-4 rounded-xl" style={{ background: 'rgba(239,68,68,0.03)', border: '1px solid rgba(239,68,68,0.1)' }}>
            <p className="text-xs font-semibold mb-2" style={{ color: '#ef4444' }}>Failed Generations</p>
            <div className="flex gap-4">
              {stats.failures.videos > 0 && <span className="text-[11px]" style={{ color: C.dim }}>Videos: <strong style={{ color: '#ef4444' }}>{stats.failures.videos}</strong></span>}
              {stats.failures.characters > 0 && <span className="text-[11px]" style={{ color: C.dim }}>Characters: <strong style={{ color: '#ef4444' }}>{stats.failures.characters}</strong></span>}
            </div>
          </div>
        )}

        {/* Recent Users */}
        {stats?.recent_users?.length > 0 && (
          <div className="p-4 rounded-xl" style={{ background: C.card, border: `1px solid ${C.border}` }}>
            <p className="text-xs font-semibold mb-3 uppercase tracking-wider" style={{ color: C.dim }}>Recent Users</p>
            <div className="space-y-2">
              {stats.recent_users.map((u, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)' }}>
                  <span className="text-[11px] font-medium" style={{ color: C.text }}>{u.email}</span>
                  <span className="text-[10px]" style={{ color: C.dim }}>{u.subscription?.tier || 'starter'}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Activity Feed */}
        <div className="p-4 rounded-xl" style={{ background: C.card, border: `1px solid ${C.border}` }}>
          <p className="text-xs font-semibold mb-3 uppercase tracking-wider" style={{ color: C.dim }}>Activity Feed</p>
          {loading ? (
            <p className="text-[11px] text-center py-8" style={{ color: C.dim }}>Loading...</p>
          ) : events.length === 0 ? (
            <p className="text-[11px] text-center py-8" style={{ color: C.dim }}>No events yet. Activity will appear as users interact with the app.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {events.map((ev, i) => {
                const iconInfo = EVENT_ICONS[ev.event_type] || EVENT_ICONS.error;
                const IconComponent = iconInfo.icon;
                return (
                  <div key={i} className="flex items-start gap-3 py-2.5 px-3 rounded-lg transition-all"
                    style={{ background: ev.read ? 'transparent' : 'rgba(168,139,250,0.03)', border: `1px solid ${ev.read ? 'transparent' : 'rgba(168,139,250,0.08)'}` }}>
                    <div className="mt-0.5 p-1.5 rounded-lg" style={{ background: `${iconInfo.color}10` }}>
                      <IconComponent size={12} weight="fill" style={{ color: iconInfo.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-medium" style={{ color: C.text }}>{ev.message}</p>
                      <p className="text-[9px] mt-0.5" style={{ color: C.dim }}>
                        {ev.created_at ? new Date(ev.created_at).toLocaleString() : ''}
                        {ev.data?.user_email ? ` — ${ev.data.user_email}` : ''}
                        {ev.data?.feature ? ` — ${ev.data.feature}` : ''}
                        {' — '}{ev.severity}
                      </p>
                    </div>
                    {!ev.read && <div className="w-1.5 h-1.5 rounded-full mt-1.5" style={{ background: '#a78bfa' }} />}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
